import { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { colors, spacing, borderRadius, typography, shadows } from '@serps/ui-kit/src/tokens';
import { Button } from '@serps/ui-kit/src/components/Button';
import { Spinner } from '@serps/ui-kit/src/components/Spinner';
import { apiGet, apiPost, type AuthUser } from '../lib/auth';
import { useLang } from '../lib/lang';
import { Toast, type ToastVariant } from '../components/Toast';

interface Props { user: AuthUser }

interface Session {
  id: string; user_id: string; role: string; school_id: string | null;
  locale: 'ms' | 'en'; started_at: string; last_message_at: string;
  message_count: number; expires_at: string;
}

interface PersistedMessage {
  id: string; session_id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string | null;
  tool_name: string | null; tool_input: string | null; tool_output_preview: string | null;
  tokens_used: number; created_at: string;
  user_feedback: 'POSITIVE' | 'NEGATIVE' | null; is_flagged: boolean;
}

interface SendResponse {
  assistantText: string;
  toolCalls: Array<{ name: string; input: unknown; preview: string; isError: boolean }>;
  tokensUsed: number;
  usage: { hourly: number; daily: number; hourlyLimit: number; dailyLimit: number };
  sessionMessageCount: number;
}

interface Usage { hourly: number; daily: number; hourlyLimit: number; dailyLimit: number }

const PROMPT_SUGGESTIONS_MS = [
  'Tunjukkan kehadiran pelajar minggu lepas.',
  'Senaraikan permohonan cuti yang menunggu.',
  'Apakah prestasi kelas 5A untuk Matematik?',
  'Jalankan laporan R02 untuk daerah Brunei-Muara.',
];
const PROMPT_SUGGESTIONS_EN = [
  'Show last week\'s student attendance.',
  'List pending leave applications.',
  'What is class 5A\'s performance in Math?',
  'Run report R02 for Brunei-Muara district.',
];

export function EduBotPage({ user }: Props) {
  const { lang } = useLang();
  const [sessions,     setSessions]     = useState<Session[]>([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [activeSession, setActiveSession] = useState<Session | null>(null);
  const [messages,     setMessages]     = useState<PersistedMessage[]>([]);
  const [pendingAssistant, setPendingAssistant] = useState<{ text: string; tools: SendResponse['toolCalls'] } | null>(null);

  const [input,        setInput]        = useState('');
  const [sending,      setSending]      = useState(false);
  const [error,        setError]        = useState<string | null>(null);
  const [toast,        setToast]        = useState<{ message: string; variant: ToastVariant } | null>(null);

  // locale drives the EduBot session language.
  // Sync it with the global lang toggle so switching BM/EN works immediately.
  const [locale,          setLocale]          = useState<'ms' | 'en'>(() => lang === 'en' ? 'en' : 'ms');
  const [usage,           setUsage]           = useState<Usage | null>(null);
  const [starting,        setStarting]        = useState(false);
  const [sessionLoading,  setSessionLoading]  = useState(false);

  // Keep locale in sync whenever the navbar language toggle changes
  useEffect(() => {
    setLocale(lang === 'en' ? 'en' : 'ms');
  }, [lang]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try { setSessions(await apiGet<Session[]>('/api/edubot/sessions')); }
    catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Gagal memuatkan sesi. / Failed to load sessions.' });
    }
    finally { setSessionsLoading(false); }
  }, []);

  const loadUsage = useCallback(async () => {
    try { setUsage(await apiGet<Usage>('/api/edubot/usage')); }
    catch { /* non-fatal */ }
  }, []);

  useEffect(() => { void loadSessions(); void loadUsage(); }, [loadSessions, loadUsage]);

  useEffect(() => {
    // Auto-scroll to bottom on new messages
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, pendingAssistant, sending]);

  async function openSession(s: Session) {
    if (sessionLoading) return;
    setSessionLoading(true);
    setActiveSession(s); setMessages([]); setPendingAssistant(null); setError(null);
    try {
      const res = await apiGet<{ session: Session; messages: PersistedMessage[] }>(`/api/edubot/sessions/${s.id}`);
      setActiveSession(res.session);
      setMessages(res.messages);
      setLocale(res.session.locale);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memuatkan sesi. / Failed to load session.');
    } finally {
      setSessionLoading(false);
    }
  }

  async function startNewSession() {
    setStarting(true); setError(null);
    try {
      const session = await apiPost<Session>('/api/edubot/sessions', { locale });
      setActiveSession(session);
      setMessages([]);
      setPendingAssistant(null);
      setSessions((prev) => [session, ...prev]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memulakan sesi. / Failed to start session.');
    } finally { setStarting(false); }
  }

  function validateInput(text: string): string | null {
    const t = text.trim();
    if (!t)                return locale === 'ms' ? 'Sila taip mesej.' : 'Please type a message.';
    if (t.length > 4000)   return locale === 'ms' ? 'Mesej terlalu panjang (maks 4000 aksara).' : 'Message too long (max 4000 chars).';
    return null;
  }

  async function handleSend() {
    const text = input.trim();
    const err  = validateInput(text);
    if (err) { setError(err); return; }
    if (!activeSession) {
      setError(locale === 'ms' ? 'Mulakan sesi dahulu.' : 'Start a session first.');
      return;
    }
    setError(null);

    // Optimistic UI: show the user message immediately
    const optimisticUser: PersistedMessage = {
      id: `pending-${Date.now()}`, session_id: activeSession.id, role: 'user',
      content: text, tool_name: null, tool_input: null, tool_output_preview: null,
      tokens_used: 0, created_at: new Date().toISOString(),
      user_feedback: null, is_flagged: false,
    };
    setMessages((prev) => [...prev, optimisticUser]);
    setInput('');
    setSending(true);

    try {
      const res = await apiPost<SendResponse>(`/api/edubot/sessions/${activeSession.id}/messages`, { message: text });
      // Reload full message log so we get real DB IDs + tool messages
      const refreshed = await apiGet<{ session: Session; messages: PersistedMessage[] }>(`/api/edubot/sessions/${activeSession.id}`);
      setActiveSession(refreshed.session);
      setMessages(refreshed.messages);
      setPendingAssistant(null);
      setUsage(res.usage);
    } catch (e) {
      // Remove optimistic message on failure
      setMessages((prev) => prev.filter((m) => m.id !== optimisticUser.id));
      const msg = e instanceof Error ? e.message : 'Gagal menghantar mesej. / Failed to send message.';
      setError(msg);
      setToast({ variant: 'error', message: msg });
    } finally { setSending(false); }
  }

  async function handleFeedback(messageId: string, feedback: 'POSITIVE' | 'NEGATIVE') {
    try {
      await apiPost(`/api/edubot/messages/${messageId}/feedback`, { feedback });
      setMessages((prev) => prev.map((m) => m.id === messageId ? { ...m, user_feedback: feedback } : m));
      setToast({ variant: 'success', message: locale === 'ms' ? 'Terima kasih atas maklum balas.' : 'Thanks for the feedback.' });
    } catch (err) {
      setToast({ variant: 'error', message: err instanceof Error ? err.message : 'Gagal hantar maklum balas.' });
    }
  }

  const suggestions = locale === 'ms' ? PROMPT_SUGGESTIONS_MS : PROMPT_SUGGESTIONS_EN;

  return (
    <>
      <div style={{ minHeight: '100vh', backgroundColor: colors.neutral.black, fontFamily: typography.fontFamily.body, padding: `${spacing.xl} ${spacing.md}` }}>
        <div style={{ maxWidth: '1100px', margin: '0 auto', display: 'grid', gridTemplateColumns: 'minmax(220px, 280px) 1fr', gap: spacing.lg }}>

          {/* ── Sidebar: sessions ──────────────────────────────────────── */}
          <aside style={{ display: 'flex', flexDirection: 'column', gap: spacing.md, minHeight: 0 }}>
            <div>
              <Link to="/" style={{ color: colors.neutral.gray400, fontSize: typography.fontSize.sm, textDecoration: 'none' }}>
                ← {locale === 'en' ? 'Home' : 'Laman Utama'}
              </Link>
            </div>
            <Button variant="primary" size="sm" fullWidth onClick={() => void startNewSession()} disabled={starting} loading={starting}>
              {starting
                ? (locale === 'en' ? 'Starting…' : 'Memulakan…')
                : (locale === 'en' ? '+ New Session' : '+ Sesi Baharu')}
            </Button>

            <div style={{ display: 'flex', gap: spacing.xs, alignItems: 'center' }}>
              <label style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>{lang === 'en' ? 'Language:' : 'Bahasa:'}</label>
              {(['ms', 'en'] as const).map((l) => (
                <button
                  key={l}
                  onClick={() => setLocale(l)}
                  disabled={starting}
                  title={activeSession ? 'Bahasa UI ditukar. Sesi baharu akan menggunakan bahasa ini. / UI language changed. New sessions will use this language.' : undefined}
                  style={{
                    padding: `${spacing.xs} ${spacing.sm}`, borderRadius: borderRadius.md, border: 'none',
                    backgroundColor: locale === l ? colors.primary.yellow : colors.neutral.gray800,
                    color: locale === l ? colors.neutral.black : colors.neutral.gray400,
                    fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold,
                    cursor: starting ? 'not-allowed' : 'pointer',
                    fontFamily: typography.fontFamily.body, opacity: starting ? 0.6 : 1,
                  }}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            {usage && (
              <div style={{ backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.lg, padding: spacing.sm }}>
                <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400, fontWeight: typography.fontWeight.semibold }}>
                  KUOTA
                </p>
                <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.white }}>
                  Jam ini: {usage.hourly}/{usage.hourlyLimit}
                </p>
                <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.white }}>
                  Hari ini: {usage.daily}/{usage.dailyLimit}
                </p>
                <div style={{ marginTop: spacing.xs, height: '4px', backgroundColor: colors.neutral.gray800, borderRadius: '2px', overflow: 'hidden' }}>
                  <div style={{
                    width: `${Math.min(100, (usage.daily / usage.dailyLimit) * 100)}%`,
                    height: '100%',
                    backgroundColor: usage.daily / usage.dailyLimit > 0.8 ? '#f87171' : colors.primary.yellow,
                  }} />
                </div>
              </div>
            )}

            <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <p style={{ margin: `0 0 ${spacing.xs}`, fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: colors.neutral.gray400 }}>
                SESI TERKINI
              </p>
              {sessionsLoading && <div style={{ display: 'flex', justifyContent: 'center', padding: spacing.sm }}><Spinner size="sm" /></div>}
              {!sessionsLoading && sessions.length === 0 && (
                <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                  Tiada sesi sebelumnya. / No prior sessions.
                </p>
              )}
              {sessions.map((s) => {
                const active = activeSession?.id === s.id;
                return (
                  <button
                    key={s.id}
                    onClick={() => void openSession(s)}
                    disabled={sessionLoading}
                    style={{
                      display: 'block', width: '100%', textAlign: 'left',
                      padding: spacing.sm, marginBottom: spacing.xs,
                      backgroundColor: active ? 'rgba(247,208,0,0.1)' : colors.neutral.charcoal,
                      border: `1px solid ${active ? colors.primary.yellow : colors.neutral.gray800}`,
                      borderRadius: borderRadius.md,
                      cursor: sessionLoading ? 'not-allowed' : 'pointer',
                      opacity: sessionLoading && !active ? 0.5 : 1,
                      fontFamily: typography.fontFamily.body,
                    }}
                  >
                    <p style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.white, fontWeight: typography.fontWeight.medium }}>
                      {s.message_count} {locale === 'en' ? 'messages' : 'mesej'} · {s.locale.toUpperCase()}
                    </p>
                    <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                      {new Date(s.last_message_at).toLocaleString('ms-BN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* ── Main: chat ─────────────────────────────────────────────── */}
          <main style={{ display: 'flex', flexDirection: 'column', minHeight: '85vh', backgroundColor: colors.neutral.charcoal, borderRadius: borderRadius.xl, overflow: 'hidden', boxShadow: shadows.lg }}>
            {/* Header */}
            <header style={{ padding: spacing.lg, borderBottom: `1px solid ${colors.neutral.gray800}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: spacing.sm, flexWrap: 'wrap' }}>
                <div>
                  <p style={{ margin: 0, fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.bold, color: colors.neutral.white }}>
                    🤖 EduBot
                  </p>
                  <p style={{ margin: `2px 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                    {locale === 'en'
                      ? `Bilingual AI assistant — powered by Claude · Read-only · ${user.email}`
                      : `Pembantu AI dwibahasa — dikuasakan oleh Claude · Baca sahaja · ${user.email}`}
                  </p>
                </div>
                {activeSession && (
                  <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                    {locale === 'en' ? 'Session' : 'Sesi'} {activeSession.id.slice(0, 8)}… · {locale === 'en' ? 'expires' : 'tamat'} {new Date(activeSession.expires_at).toLocaleTimeString(locale === 'en' ? 'en-GB' : 'ms-BN', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                )}
              </div>
            </header>

            {/* Conversation */}
            <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: spacing.lg, display: 'flex', flexDirection: 'column', gap: spacing.md }}>
              {!activeSession && (
                <div style={{ textAlign: 'center', padding: spacing.xl, color: colors.neutral.gray400 }}>
                  <div style={{ fontSize: '48px', marginBottom: spacing.sm }}>🤖</div>
                  <p style={{ margin: 0, fontWeight: typography.fontWeight.semibold, color: colors.neutral.white }}>
                    {locale === 'en' ? 'Welcome to EduBot' : 'Selamat datang ke EduBot'}
                  </p>
                  <p style={{ margin: `${spacing.xs} 0 ${spacing.lg}`, fontSize: typography.fontSize.sm }}>
                    {locale === 'en'
                      ? 'Start a new session to ask about students, educators, attendance, grades, and more.'
                      : 'Mulakan sesi baharu untuk bertanya tentang pelajar, pendidik, kehadiran, markah, dan banyak lagi.'}
                  </p>
                  <Button variant="primary" size="md" onClick={() => void startNewSession()} disabled={starting} loading={starting}>
                    {starting
                      ? (locale === 'en' ? 'Starting…' : 'Memulakan…')
                      : (locale === 'en' ? '+ Start Session' : '+ Mulakan Sesi')}
                  </Button>
                </div>
              )}

              {activeSession && messages.length === 0 && !sending && (
                <div style={{ padding: spacing.lg, textAlign: 'center', color: colors.neutral.gray400 }}>
                  <div style={{ fontSize: '32px', marginBottom: spacing.sm }}>💬</div>
                  <p style={{ margin: 0, fontSize: typography.fontSize.sm }}>
                    {locale === 'ms' ? 'Cuba salah satu daripada cadangan ini:' : 'Try one of these suggestions:'}
                  </p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: spacing.xs, justifyContent: 'center', marginTop: spacing.md }}>
                    {suggestions.map((s) => (
                      <button key={s} disabled={sending} onClick={() => setInput(s)} style={{
                        padding: `${spacing.xs} ${spacing.sm}`, backgroundColor: colors.neutral.black,
                        border: `1px solid ${colors.neutral.gray800}`, borderRadius: borderRadius.full,
                        color: colors.neutral.gray400, cursor: sending ? 'not-allowed' : 'pointer',
                        fontFamily: typography.fontFamily.body, fontSize: typography.fontSize.xs,
                      }}>{s}</button>
                    ))}
                  </div>
                </div>
              )}

              {messages.filter((m) => m.role !== 'tool' || m.is_flagged).map((m) => {
                if (m.role === 'user') {
                  return (
                    <div key={m.id} style={{ alignSelf: 'flex-end', maxWidth: '78%' }}>
                      <div style={{ backgroundColor: colors.primary.yellow, color: colors.neutral.black, padding: `${spacing.sm} ${spacing.md}`, borderRadius: borderRadius.xl, fontSize: typography.fontSize.sm, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                        {m.content}
                      </div>
                    </div>
                  );
                }
                if (m.role === 'tool') {
                  return (
                    <div key={m.id} style={{ alignSelf: 'flex-start', maxWidth: '78%' }}>
                      <div style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.3)', padding: `${spacing.xs} ${spacing.sm}`, borderRadius: borderRadius.md, fontSize: typography.fontSize.xs, color: '#f87171' }}>
                        🛠 {m.tool_name} — {m.tool_output_preview}
                      </div>
                    </div>
                  );
                }
                // assistant
                return (
                  <div key={m.id} style={{ alignSelf: 'flex-start', maxWidth: '78%' }}>
                    <div style={{ backgroundColor: colors.neutral.black, padding: `${spacing.sm} ${spacing.md}`, borderRadius: borderRadius.xl, fontSize: typography.fontSize.sm, color: colors.neutral.white, whiteSpace: 'pre-wrap', lineHeight: 1.6, border: `1px solid ${colors.neutral.gray800}` }}>
                      {m.content}
                    </div>
                    <div style={{ display: 'flex', gap: spacing.xs, marginTop: spacing.xs, paddingLeft: spacing.sm }}>
                      <button onClick={() => void handleFeedback(m.id, 'POSITIVE')} disabled={m.user_feedback !== null}
                        style={feedbackBtnStyle(m.user_feedback === 'POSITIVE')}>👍</button>
                      <button onClick={() => void handleFeedback(m.id, 'NEGATIVE')} disabled={m.user_feedback !== null}
                        style={feedbackBtnStyle(m.user_feedback === 'NEGATIVE')}>👎</button>
                      {m.tokens_used > 0 && (
                        <span style={{ fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                          {m.tokens_used} tokens
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}

              {sending && (
                <div style={{ alignSelf: 'flex-start', display: 'flex', alignItems: 'center', gap: spacing.sm, color: colors.neutral.gray400 }}>
                  <Spinner size="sm" />
                  <span style={{ fontSize: typography.fontSize.xs }}>
                    {locale === 'ms' ? 'EduBot sedang berfikir…' : 'EduBot is thinking…'}
                  </span>
                </div>
              )}

              {pendingAssistant && pendingAssistant.tools.length > 0 && (
                <div style={{ alignSelf: 'flex-start' }}>
                  {pendingAssistant.tools.map((t, i) => (
                    <p key={i} style={{ margin: 0, fontSize: typography.fontSize.xs, color: colors.neutral.gray400 }}>
                      🔧 {t.name}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {/* Composer */}
            <footer style={{ padding: spacing.lg, borderTop: `1px solid ${colors.neutral.gray800}` }}>
              {error && (
                <p style={{ margin: `0 0 ${spacing.xs}`, color: '#f87171', fontSize: typography.fontSize.xs }}>{error}</p>
              )}
              <div style={{ display: 'flex', gap: spacing.sm, alignItems: 'flex-end' }}>
                <textarea
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void handleSend(); } }}
                  placeholder={locale === 'ms' ? 'Taip mesej anda… (Shift+Enter untuk baris baharu)' : 'Type your message… (Shift+Enter for new line)'}
                  disabled={!activeSession || sending}
                  maxLength={4000}
                  rows={2}
                  style={{
                    flex: 1, backgroundColor: colors.neutral.black, border: `1px solid ${colors.neutral.gray800}`,
                    borderRadius: borderRadius.md, color: colors.neutral.white, padding: spacing.sm,
                    fontSize: typography.fontSize.sm, fontFamily: typography.fontFamily.body, outline: 'none', resize: 'vertical',
                  }}
                />
                <Button variant="primary" size="md" onClick={() => void handleSend()} disabled={!activeSession || sending || !input.trim()} loading={sending}>
                  {sending ? '…' : '↑'}
                </Button>
              </div>
              <p style={{ margin: `${spacing.xs} 0 0`, fontSize: typography.fontSize.xs, color: colors.neutral.gray600 }}>
                {input.length}/4000 · {locale === 'ms' ? 'EduBot hanya membaca data. Untuk membuat perubahan, gunakan portal SERPS.' : 'EduBot is read-only. Use the SERPS portal to make changes.'}
              </p>
            </footer>
          </main>
        </div>
      </div>
      {toast && <Toast message={toast.message} variant={toast.variant} onDismiss={() => setToast(null)} />}
    </>
  );
}

function feedbackBtnStyle(active: boolean): React.CSSProperties {
  return {
    padding: '2px 6px',
    backgroundColor: active ? colors.primary.yellow : 'transparent',
    color: active ? colors.neutral.black : colors.neutral.gray400,
    border: `1px solid ${active ? colors.primary.yellow : colors.neutral.gray800}`,
    borderRadius: borderRadius.md, fontSize: typography.fontSize.xs,
    cursor: active ? 'default' : 'pointer', fontFamily: typography.fontFamily.body,
  };
}
